import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import { Icon, Popconfirm, Checkbox, Modal, ConfigProvider } from 'antd';
import PropTypes from 'prop-types';
import update from 'immutability-helper';

import enUS from 'antd/lib/locale/en_US';

import Table from './antTable.style';
import Select, { SelectOption as Option } from '../elements/select';
import Button from '../Button';
import Block from '../Block';
import Span from '../Span';
import Anchor from '../Anchor';
import Box from '../utility/box';
import FilterIcon from './filterIcon';
import { EditableFormRow, EditableCell, EditableContext } from './editablecell';
import { DragableBodyRow, EditableDragableFormRow } from './draggable';
import { request } from '../../../axios/request';
import { setMainDenialReason } from 'actions/packageDenialReasons';

import { sendRequest } from '../misc/network';
import { httpMethods, responseTypes } from '../../../appConstants';
import { parameterizedString, getActionParams } from 'theme/utils';
import { isEmpty } from 'lodash';
import getRenderer from './renderers';
import formatData from './formatters';

import {
  getRandom,
  serializeObjectWithDot,
  shallowEqual,
  createURL,
  arrayEqual,
  getObjectFromString,
  isDefined,
  setObjectFromString,
  isCommandAvailable,
} from '../../../utils';
import {
  defaultHorizontalScroll,
  defaultPagination,
  defaultVerticalScroll,
  defaultColumnProps,
  locale,
  paginationPosition,
  draggableColumn,
  pageSizeLimitForMultiplePagination,
} from './constants';

import './style.css';

const $ST = require(`plugins/${process.env.REACT_APP_BRAND_CODE}/platformConfigs/staticTextMaps/datatable`);
const {
  LANGUAGE: STORE_LANGUAGE,
  BRAND,
} = require(`plugins/${process.env.REACT_APP_BRAND_CODE}/platformConfigs/app.json`);
class Datatable extends PureComponent {
  constructor(props) {
    super(props);
    this.onDatatableAction = this.onDatatableAction.bind(this);
    const {
      pagination = {},
      rowSelection,
      horizontalScroll,
      horizontalScrollSize,
      verticalScroll,
      verticalScrollSize,
      locale: localeProps = {},
      dataSource,
    } = props;
    this.state = {
      selectedRowKeys: [],
      dataSource: dataSource || [],
      pagination:
        pagination === false
          ? false
          : {
              ...defaultPagination,
              ...pagination,
              onShowSizeChange: this.onShowSizeChange,
              total: dataSource && dataSource.length,
              position:
                dataSource &&
                dataSource.length > pageSizeLimitForMultiplePagination &&
                (pagination.pageSize
                  ? pagination.pageSize >= pageSizeLimitForMultiplePagination
                  : defaultPagination.pageSize >=
                    pageSizeLimitForMultiplePagination)
                  ? paginationPosition.both
                  : paginationPosition.bottom,
            },
      loading: false,
      editingKey: '',
      confirmVisible: false,
      modalVisibility: false,
      filters: [],
      selectedFilters: [],
      filterColumns: [],
      selected: false,
    };
    this.locale = { ...locale, ...localeProps };
    this.rowSelection = rowSelection
      ? {
          ...rowSelection,
          onSelectAll: this.onSelectAll,
          onChange: this.onRowSelection,
        }
      : null;
    this.scroll =
      horizontalScroll || verticalScroll
        ? {
            x: horizontalScrollSize
              ? horizontalScrollSize
              : horizontalScroll
              ? defaultHorizontalScroll
              : null,
            y: verticalScrollSize
              ? verticalScrollSize
              : verticalScroll
              ? defaultVerticalScroll
              : null,
          }
        : undefined;
  }

  handleDelete = (key) => {
    const { rowKey, onChangeDataSource, onDeleteRow } = this.props;
    const dataSource = [...this.state.dataSource];
    let deletedItem = dataSource.find((item) => item[rowKey] === key);
    this.setState(
      {
        dataSource: dataSource.filter((item) => item[rowKey] !== key),
      },
      () => {
        if (onChangeDataSource) onChangeDataSource(this.state.dataSource);
        if (onChangeDataSource) onChangeDataSource(this.state.dataSource);
        if (onDeleteRow) onDeleteRow(deletedItem);
      }
    );
  };

  onSelectAll = (selected) => this.setState({ selected });

  createNewData = () => {
    const columnKeys = this.columns
      .filter((column) => !column.systemProp)
      .map((column) => column.dataIndex);
    let result = {};
    for (const key of columnKeys) {
      result = { ...result, [key]: '' };
    }
    result[this.props.rowKey] = getRandom();
    return serializeObjectWithDot(result);
  };

  handleSaveCell = (row) => {
    const { rowKey } = this.props;
    const newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row[rowKey] === item[rowKey]);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    const { selectedRows } = this.state;
    let newRows = null;
    if (selectedRows && selectedRows.length) {
      const index = selectedRows.findIndex(
        (item) => item[rowKey] === row[rowKey]
      );
      newRows = [
        ...selectedRows.slice(0, index),
        row,
        ...selectedRows.slice(index + 1, selectedRows.length),
      ];
    }
    this.setState(
      (prevState) => ({
        dataSource: newData,
        selectedRows: newRows ? newRows : prevState.selectedRows,
      }),
      () => {
        const { selectedRowKeys, selectedRows } = this.state;
        this.onRowSelection(selectedRowKeys, selectedRows);
      }
    );
  };

  isEditing = (record) => record[this.props.rowKey] === this.state.editingKey;

  cancel = () => {
    this.setState({ editingKey: '' });
  };

  handleSaveRow(form, key) {
    const { rowKey, onChangeDataSource, onChangeRow } = this.props;
    form.validateFields((error, row) => {
      if (error) {
        return;
      }
      const newData = [...this.state.dataSource];
      const index = newData.findIndex((item) => key === item[rowKey]);
      const resultAction = () => {
        if (onChangeDataSource) onChangeDataSource(this.state.dataSource);
        if (onChangeRow) onChangeRow(row);
      };
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        this.setState({ dataSource: newData, editingKey: '' }, resultAction);
      } else {
        newData.push(row);
        this.setState({ dataSource: newData, editingKey: '' }, resultAction);
      }
    });
  }

  edit(key) {
    this.setState({ editingKey: key });
  }

  onShowSizeChange = (current, pageSize) => {
    this.setState((prevState) => ({
      pagination:
        prevState.pagination === false
          ? false
          : { ...prevState.pagination, pageSize },
    }));
  };

  onRowSelection = (selectedRowKeys, selectedRows) => {
    const {
      rowSelection: { onRowSelection },
    } = this.props;
    this.setState(
      {
        selectedRowKeys,
        selectedRows,
      },
      () => {
        if (onRowSelection) onRowSelection(selectedRowKeys, selectedRows);
      }
    );
  };

  componentWillReceiveProps(nextProps) {
    const { filter, formFilters, dataSource, remote } = this.props;
    if (
      !shallowEqual(filter, nextProps.filter) ||
      !arrayEqual(
        formFilters.map((formFilter) => formFilter.key),
        nextProps.formFilters.map((formFilter) => formFilter.key)
      )
    )
      this.fetchData({
        filter: nextProps.filter,
        formFilters: nextProps.formFilters,
      });
    if (!remote && !shallowEqual(dataSource, nextProps.dataSource))
      this.setState((prevState) => ({
        dataSource: nextProps.dataSource,
        pagination:
          prevState.pagination === false
            ? false
            : {
                ...prevState.pagination,
                total: dataSource ? dataSource.length : 0,
                current: 1,
                position:
                  dataSource &&
                  dataSource.length > pageSizeLimitForMultiplePagination &&
                  prevState.pagination.pageSize >=
                    pageSizeLimitForMultiplePagination
                    ? paginationPosition.both
                    : paginationPosition.bottom,
              },
      }));
  }

  componentDidMount() {
    const { filter, formFilters, filtersURL } = this.props;
    this.fetchData({
      filter: filter,
      formFilters: formFilters,
    });
    if (filtersURL) {
      this.fetchFilters(filtersURL);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.url !== this.props.url) {
      this.fetchData(this.props);
    }
  }

  fetchFilters = (url) => {
    const oldFilters = this.getPreviousFilters();
    this.setState(
      (prevState) => ({
        ...prevState,
        filters: oldFilters,
        selectedFilters: oldFilters,
      }),
      () => {
        this.applyFilters();
      }
    );
    request({
      url,
      method: 'get',
    }).then((response) => {
      this.setState((prevState) => ({
        ...prevState,
        filters: response.data.results,
      }));
    });
  };

  fetchData = ({
    page = 1,
    sortField,
    sortOrder,
    filter,
    formFilters,
    sorter,
  } = {}) => {
    const { url, onChangeTotalCount, remote, sourceKey, onGetResponse } =
      this.props;
    const { pagination } = this.state;
    const pageSize = pagination === false ? null : pagination.pageSize,
      pageOrder = pagination === false ? null : page;
    if (!remote) return;
    this.setState({ loading: true });
    let formFile = null;
    const hasFormData = formFilters && !!formFilters.length;
    if (hasFormData) {
      formFile = new FormData();
      formFile.append('filename', formFilters[0].file);
    }
    const queryObject = {
      ...filter,
      ...(pageSize ? { limit: pageSize } : {}),
      ...(pageOrder ? { page: pageOrder } : {}),
      ...(sortOrder
        ? { sort: sortOrder === 'ascend' ? sortField : '-' + sortField }
        : {}),
    };
    sendRequest({
      url: hasFormData ? createURL(url, [], queryObject) : url,
      params: hasFormData ? formFile : queryObject,
      method: hasFormData ? httpMethods.POST : httpMethods.GET,
      headers: hasFormData ? { 'Override-Method': 'GET' } : {},
      onSuccess: (result) => {
        if (onChangeTotalCount) onChangeTotalCount(result.count);
        if (onGetResponse) onGetResponse(responseTypes.success, result);
        this.setState((prevState) => ({
          dataSource: getObjectFromString(sourceKey, result),
          next: result.next,
          loading: false,
          pagination:
            prevState.pagination === false
              ? false
              : {
                  ...prevState.pagination,
                  total: result.count,
                  current: page,
                  position:
                    result.count > pageSizeLimitForMultiplePagination &&
                    prevState.pagination.pageSize >=
                      pageSizeLimitForMultiplePagination
                      ? paginationPosition.both
                      : paginationPosition.bottom,
                },
        }));
      },
      onFail: (error) => {
        if (onGetResponse) onGetResponse(responseTypes.fail, error);
        this.setState({
          dataSource: [],
          loading: false,
        });
      },
      onFinally: () => {
        this.clickSelectAllCheckBox();
      },
    });
  };

  clickSelectAllCheckBox = () => {
    const checkboxEl = document.getElementsByClassName('ant-checkbox-input');
    const firstCheckboxEl = checkboxEl.length && checkboxEl[0];
    if (firstCheckboxEl) {
      firstCheckboxEl.click();
    }
  };

  handleTableChange = (pagination, filters, sorter) => {
    const pager = { ...this.state.pagination };
    const { filter, getSorting = () => {} } = this.props;
    pager.current = pagination.current;
    this.setState(
      (prevState) => ({
        pagination:
          prevState.pagination === false
            ? false
            : { ...prevState.pagination, current: pagination.current },
      }),
      () => {
        this.fetchData({
          filter,
          size: pagination.pageSize,
          page: pagination.current,
          sortField: sorter.field,
          sortOrder: sorter.order,
        });
        getSorting({
          sortField: sorter.field,
          sortOrder: sorter.order,
        });
      }
    );
  };

  handleActionSelectBoxChange = (id) => {
    this.setState({ actionSelectBoxId: id });
  };

  onDatatableAction = () => {
    const { currentAction: currentActionID, selectedRowKeys } = this.state;
    const { actionButtons } = this.props;
    const currentAction = actionButtons.find(
      (button) => button.id === currentActionID
    );
    if (
      !currentAction ||
      (currentAction.selectionRequired
        ? !selectedRowKeys || !selectedRowKeys.length
        : false)
    )
      return;
    const resultAction = currentAction.reloadData ? this.fetchData : null;

    const formatValue =
      selectedRowKeys.length > 1
        ? selectedRowKeys.join(',')
        : selectedRowKeys[0];
    if (currentAction.customAction) {
      currentAction.customAction(currentAction);
      return;
    } else if (currentAction.confirm) {
      Modal.confirm({
        title: currentAction.title,
        content: currentAction.content.replace('{0}', selectedRowKeys.length),
        okText: 'Evet',
        cancelText: 'Hayır',
        onOk: () => {
          doCurrentAction();
        },
        onCancel: () => {
          this.hideConfirmModal();
        },
      });
    } else {
      doCurrentAction();
    }

    function paramsMapper(params, iterator, iteratorParam) {
      return {
        ...params,
        [iteratorParam]: iterator,
      };
    }

    function doCurrentAction() {
      let param = { ...currentAction.params };
      param = setObjectFromString(
        param,
        currentAction.bodyKey,
        selectedRowKeys
      );
      if (currentAction.loop) {
        for (const iterator of selectedRowKeys) {
          if (currentAction.iteratorParam) {
            currentAction.params[currentAction.iteratorParam] = iterator;
          }
          sendRequest({
            url:
              httpMethods[currentAction.httpType] === httpMethods.GET
                ? currentAction.url.replace('{0}', iterator)
                : currentAction.replaceUrlViaKey
                ? currentAction.url.replace('{0}', iterator)
                : currentAction.url,
            method: httpMethods[currentAction.httpType],
            params:
              httpMethods[currentAction.httpType] === httpMethods.GET
                ? currentAction.params
                : currentAction.params &&
                  paramsMapper(
                    currentAction.params,
                    iterator,
                    currentAction.iteratorParam
                  ),
            onSuccess: (result) => {
              if (currentAction.onSuccess) currentAction.onSuccess(result);
              selectedRowKeys.length = 0;
            },
            onFail: () => {
              if (currentAction.onFail) currentAction.onFail();
            },
            onFinally: () => {
              if (
                resultAction &&
                selectedRowKeys.indexOf(iterator) === selectedRowKeys.length - 1
              )
                resultAction();
            },
          });
        }
      } else {
        sendRequest({
          url:
            httpMethods[currentAction.httpType] === httpMethods.GET
              ? currentAction.url.replace('{0}', formatValue)
              : currentAction.replaceUrlViaKey
              ? currentAction.url.replace('{0}', formatValue)
              : currentAction.url,
          method: httpMethods[currentAction.httpType],
          params:
            httpMethods[currentAction.httpType] === httpMethods.GET
              ? currentAction.params
              : param,
          onBegin: () => {
            if (currentAction.onBegin) currentAction.onBegin();
          },
          onSuccess: (result) => {
            if (currentAction.onSuccess) currentAction.onSuccess(result);
            if (resultAction) resultAction();
          },
          onFail: () => {
            if (currentAction.onFail) currentAction.onFail();
          },
          onFinally: () => {
            if (currentAction.onFinally) currentAction.onFinally();
          },
        });
      }
    }
  };

  actionElements = (order) => {
    const { actionSelectBoxId, selectedRowKeys, pagination } = this.state;
    const {
      actionButtons,
      actionButtonText,
      onOpenDenialReasonsModal,
      setMainDenialReason,
    } = this.props;
    const buttons = actionButtons.filter((button) => button.selectionRequired);

    const selectBoxAction = buttons.find((button) => {
      const { id } = button;
      return id === actionSelectBoxId;
    });
    if (!buttons || !buttons.length) return null;
    const options = buttons.map((button, index) => (
      <Option
        key={order * buttons.length + index + 1}
        value={button.id}
        disabled={button.disabled}
      >
        {button.label}
      </Option>
    ));
    const isPaginationActive =
      pagination &&
      pagination.total > pagination.pageSize &&
      pagination.position === paginationPosition.both;
    const actionButtonClass = isPaginationActive
      ? 'table-operations'
      : 'table-operations-default';
    const isSelectBoxActionClickable =
      selectBoxAction &&
      selectBoxAction.selectionRequired &&
      !selectBoxAction.pendingAnyAction &&
      selectedRowKeys &&
      selectedRowKeys.length;

    const hasBrandValue = ['FLO-FAS', 'SPORJINAL', 'SPX'].includes(BRAND);

    return (
      <Block className={actionButtonClass}>
        <Select
          className='select'
          onChange={this.handleActionSelectBoxChange}
          placeholder={$ST.SELECT_ACTION}
        >
          {options}
        </Select>
        <Button
          type='primary'
          disabled={!isSelectBoxActionClickable}
          onClick={() => {
            const isPackageDenialReasonsEmpty = isEmpty(
              this.props.packageDenialReasons
            );

            if (!hasBrandValue && isPackageDenialReasonsEmpty) {
              this.handleActionClick(selectBoxAction && selectBoxAction.id);
              return;
            }

            const actionParams =
              getActionParams(selectBoxAction.id, actionButtons) || {};

            setMainDenialReason(actionParams);
            onOpenDenialReasonsModal();
          }}
          className='btn btn-primary'
        >
          {actionButtonText}
        </Button>
      </Block>
    );
  };

  handleActionClick = (commandId) => {
    const { onActionClick, actionButtons } = this.props;
    onActionClick(getActionParams(commandId, actionButtons));
  };

  openModal = () => {
    this.setState({ modalVisibility: true });
  };

  closeModal = () => {
    this.setState({ modalVisibility: false }, () => {
      localStorage.setItem(
        'oms-filters',
        JSON.stringify(this.state.selectedFilters)
      );
      this.applyFilters();
    });
  };

  getPreviousFilters = () => {
    const oldFilters = localStorage.getItem('oms-filters');
    if (oldFilters !== null) {
      return JSON.parse(oldFilters);
    }
    return [];
  };

  applyFilters = () => {
    const { filterKey } = this.props;
    const { filters, selectedFilters } = this.state;
    const indexKey = filterKey
      ? `${filterKey}.product.attributes`
      : 'product.attributes';
    const filterColumns =
      filters &&
      !!filters.length &&
      filters
        .filter(
          (item) =>
            !!selectedFilters.find(
              ({ key, checked }) => key === item.key && checked
            )
        )
        .map((filter, index) => ({
          key: 100 + index,
          dataIndex: `${indexKey}.${filter.key}`,
          title: filter.name,
          active: true,
        }));

    this.setState({ filterColumns, selectedFilters });
  };

  handleCheckboxChange = (e, key) => {
    const { name, checked } = e.target;
    if (checked) {
      this.setState((prevState) => ({
        selectedFilters: [...prevState.selectedFilters, { key, name, checked }],
      }));
    } else {
      this.setState((prevState) => ({
        selectedFilters: prevState.selectedFilters.filter(
          (filter) => filter.name !== name
        ),
      }));
    }
  };

  renderFilters = () => {
    const { filters, selectedFilters } = this.state;

    const getSelectedValue = (key) => {
      const index = selectedFilters.findIndex((flt) => flt.key === key);
      if (index > -1) {
        return true;
      }
      return false;
    };

    return (
      <div className='filter-wrapper'>
        {filters &&
          !!filters.length &&
          filters.map((filter) => {
            return (
              <div className='checkbox-item' key={filter.key}>
                <Checkbox
                  name={filter.name}
                  checked={getSelectedValue(filter.key)}
                  onChange={(e) => this.handleCheckboxChange(e, filter.key)}
                >
                  {filter.name}
                </Checkbox>
              </div>
            );
          })}
      </div>
    );
  };

  renderFilterButton = () => {
    const { modalVisibility } = this.state;
    return (
      <Fragment>
        <Button type='primary' onClick={this.openModal} className='icon-filter'>
          <FilterIcon />
        </Button>
        <Modal
          visible={modalVisibility}
          onCancel={this.closeModal}
          closable
          title={$ST.FILTER_PRODUCTS}
          centered
          footer={[
            <Button key='submit' type='primary' onClick={this.closeModal}>
              {$ST.APPLY}
            </Button>,
          ]}
          width='750px'
        >
          {this.renderFilters()}
        </Modal>
      </Fragment>
    );
  };

  actionButtons = () => {
    const { actionButtons, filtersURL } = this.props;
    const { pagination } = this.state;
    const filteredButtons = actionButtons.filter(
      (button) =>
        !button.selectionRequired && isCommandAvailable(button.command)
    );
    const isPaginationActive =
      pagination &&
      pagination.total > pagination.pageSize &&
      pagination.position === paginationPosition.both;
    const actionButtonClass = isPaginationActive
      ? 'button-operations'
      : 'button-operations-default';
    const buttons = filteredButtons.map((button) => {
      const { id, label, type, pendingAnyAction } = button;
      return (
        <React.Fragment>
          {filteredButtons && filteredButtons.length && (
            <Button
              disabled={pendingAnyAction}
              key={id}
              onClick={() => this.handleActionClick(id)}
              type={type}
            >
              {label}
            </Button>
          )}
        </React.Fragment>
      );
    });

    return (
      <Fragment>
        <Block className={actionButtonClass}>
          {filtersURL && this.renderFilterButton()}
          {buttons}
          {this.props.children}
        </Block>
        <Block className='clear' />
      </Fragment>
    );
  };

  hideConfirmModal = () => {
    this.setState({ confirmVisible: false });
  };

  onRow = (row, index) => {
    const { onRowClick } = this.props;
    return {
      onClick: (event) => {
        if (onRowClick) onRowClick(row, index, event);
      },
      index,
      moveRow: this.moveRow,
    };
  };

  moveRow = (dragIndex, hoverIndex) => {
    const { dataSource } = this.state;
    const dragRow = dataSource[dragIndex];

    this.setState(
      update(this.state, {
        dataSource: {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        },
      }),
      () => {
        const { onDrag } = this.props;
        if (onDrag)
          onDrag(
            { ...dataSource[dragIndex], index: dragIndex },
            { ...dataSource[hoverIndex], index: hoverIndex }
          );
      }
    );
  };

  getColumns = () => {
    const { draggable, columns, effaceable, saveable, rowKey } = this.props;
    const { filterColumns } = this.state;
    this.columns = [
      ...columns.map((column) => ({ ...defaultColumnProps, ...column })),
      ...(filterColumns.length
        ? filterColumns.map((column) => ({ ...defaultColumnProps, ...column }))
        : {}),
    ];
    if (draggable) this.columns.unshift(draggableColumn);
    if (effaceable)
      this.columns.push({
        title: 'Delete',
        dataIndex: 'effaceable',
        systemProp: true,
        render: (text, record) =>
          this.state.dataSource.length >= 1 ? (
            <Popconfirm
              title='Sure to delete?'
              onConfirm={() => this.handleDelete(record[rowKey])}
            >
              <Anchor>Delete</Anchor>
            </Popconfirm>
          ) : null,
      });

    if (saveable)
      this.columns.push({
        title: 'Save',
        dataIndex: 'saveable',
        render: (text, record) => {
          const { editingKey } = this.state;
          const editable = this.isEditing(record);
          return (
            <Block>
              {editable ? (
                <Span>
                  <EditableContext.Consumer>
                    {(form) => (
                      <Anchor
                        onClick={() => this.handleSaveRow(form, record[rowKey])}
                        style={{ marginRight: 8 }}
                      >
                        Save
                      </Anchor>
                    )}
                  </EditableContext.Consumer>
                  <Popconfirm
                    title='Sure to cancel?'
                    onConfirm={() => this.cancel(record[rowKey])}
                  >
                    <Anchor>Cancel</Anchor>
                  </Popconfirm>
                </Span>
              ) : (
                <Anchor
                  disabled={editingKey !== ''}
                  onClick={() => this.edit(record[rowKey])}
                >
                  Edit
                </Anchor>
              )}
            </Block>
          );
        },
      });
    return this.columns.map((col) => {
      const column = { ...col };
      if (!column.render && (column.formatters || column.renderer)) {
        column.renderer = column.renderer || {};
        column.formatters = column.formatters || [];
        column.render = (cellData, rowData) =>
          getRenderer(
            column.renderer.type,
            column.renderer.props,
            formatData(column.formatters, cellData, rowData),
            rowData
          );
      }
      if (!column.editable) {
        return column;
      }
      return {
        ...column,
        onCell: (record) => ({
          record,
          inputType: column.inputType,
          editable: column.editable,
          dataIndex: column.dataIndex,
          title: column.title,
          options: column.options,
          url: column.url,
          valueKeyName: column.valueKeyName,
          labelKeyName: column.labelKeyName,
          objectKey: column.objectKey,
          checkCellValue: column.checkCellValue,
          handleSave: this.handleSaveCell,
          isEditing: this.isEditing(record),
        }),
      };
    });
  };

  render() {
    const { pagination, loading, dataSource } = this.state;
    const {
      onRowClick,
      draggable,
      loading: isLoading,
      ...otherProps
    } = this.props;
    const hasData = dataSource && !!dataSource.length;
    const columns = this.getColumns();
    const editable = this.columns.findIndex((column) => column.editable) !== -1;
    const components = {
      body: {
        row:
          draggable && editable
            ? EditableDragableFormRow
            : draggable
            ? DragableBodyRow
            : EditableFormRow,
        cell: EditableCell,
      },
    };
    const loadingValue = isDefined(isLoading) ? isLoading : loading;
    const actionElements = this.actionElements(1);
    const actionButtons = this.actionButtons();
    const isPaginationActive =
      pagination && dataSource && pagination.total > pagination.pageSize;
    return (
      <Block
        className={
          isPaginationActive ? 'pagination-active' : 'pagination-disable'
        }
      >
        {hasData && actionElements}
        {hasData && actionButtons}
        <Table
          {...otherProps}
          rowClassName={onRowClick ? 'pointer' : ''}
          components={components}
          onChange={this.handleTableChange}
          loading={loadingValue}
          dataSource={!loading && dataSource}
          rowSelection={this.rowSelection}
          scroll={this.scroll}
          pagination={pagination}
          locale={this.locale}
          columns={columns}
          onRow={this.onRow}
        />
        {pagination &&
          pagination.position === paginationPosition.both &&
          hasData &&
          this.actionElements(2)}
      </Block>
    );
  }
}

Datatable.defaultProps = {
  formFilters: [],
  sourceKey: 'results',
  actionButtonText: $ST.APPLY,
  remote: false,
  className: 'isoSimpleTable',
  actionButtons: [],
  columns: [],
};

Datatable.propTypes = {
  horizontalScroll: PropTypes.bool,
  horizontalScrollSize: PropTypes.number,
  verticalScroll: PropTypes.bool,
  verticalScrollSize: PropTypes.number,
  locale: PropTypes.object,
  dataSource: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number,
      dataIndex: PropTypes.string.isRequired,
      title: PropTypes.string,
      sorter: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      fixed: PropTypes.oneOf(['left', 'right']),
      render: PropTypes.func,
      editable: PropTypes.bool,
      inputType: PropTypes.string,
    })
  ).isRequired,
  effaceable: PropTypes.bool,
  saveable: PropTypes.bool,
  rowKey: PropTypes.string.isRequired,
  onChangeDataSource: PropTypes.func,
  rowSelection: PropTypes.object,
  filter: PropTypes.object,
  formFilters: PropTypes.array,
  url: PropTypes.string,
  onChangeTotalCount: PropTypes.func,
  sourceKey: PropTypes.string,
  actionButtons: PropTypes.array,
  actionButtonText: PropTypes.string,
  onRowClick: PropTypes.func,
  remote: PropTypes.bool,
  draggable: PropTypes.bool,
  onDrag: PropTypes.func,
  onGetResponse: PropTypes.func,
  onDeleteRow: PropTypes.func,
  onChangeRow: PropTypes.func,
  getSorting: PropTypes.func,
  filterKey: PropTypes.string,
};

export default Datatable;

class DatatableBoxInstance extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      totalCount: props.dataSource ? props.dataSource.length : 0,
    };
  }
  onChangeTotalCount = (totalCount) => {
    this.setState({
      totalCount,
    });
  };

  render() {
    const { totalCount } = this.state;
    const { exportable, exportButtonClick, subtitle } = this.props;
    const totalCountWithIcon = totalCount ? (
      <Fragment>
        <span className='dblock mb15'>
          {parameterizedString($ST.TOTAL_COUNT, totalCount)}
        </span>
        {exportable && (
          <Icon
            onClick={exportButtonClick}
            type='download'
            className='icon-download'
          />
        )}
      </Fragment>
    ) : (
      ''
    );
    const DT = Datatable;
    return (
      <Box title={subtitle} subtitle={totalCountWithIcon}>
        <ConfigProvider locale={STORE_LANGUAGE === 'en' ? enUS : false}>
          <DT {...this.props} onChangeTotalCount={this.onChangeTotalCount} />
        </ConfigProvider>
      </Box>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    packageDenialReasons: state.packageDenialReasons.packageDenialReasons,
  };
};

const mapDispatchToProps = {
  setMainDenialReason,
};

export const DatatableBox = connect(
  mapStateToProps,
  mapDispatchToProps
)(DatatableBoxInstance);



// WEBPACK FOOTER //
// ./src/components/ui-ant/Datatable/index.js